import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { composerExtensions, unsupportedMarkup } from './composerExtensions';
import UndoIcon from '@mui/icons-material/UndoRounded';
import RedoIcon from '@mui/icons-material/RedoRounded';
import BoldIcon from '@mui/icons-material/FormatBoldRounded';
import ItalicIcon from '@mui/icons-material/FormatItalicRounded';
import UnderlineIcon from '@mui/icons-material/FormatUnderlinedRounded';
import LeftIcon from '@mui/icons-material/FormatAlignLeftRounded';
import CenterIcon from '@mui/icons-material/FormatAlignCenterRounded';
import RightIcon from '@mui/icons-material/FormatAlignRightRounded';
import ListIcon from '@mui/icons-material/FormatListBulletedRounded';
import NumberIcon from '@mui/icons-material/FormatListNumberedRounded';
import QuoteIcon from '@mui/icons-material/FormatQuoteRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import UnlinkIcon from '@mui/icons-material/LinkOffRounded';
import TableIcon from '@mui/icons-material/TableChartRounded';
import LineIcon from '@mui/icons-material/HorizontalRuleRounded';
import CanvasInspector from './CanvasInspector';
import { loadEditorDocument } from './composerDocument';
import { templateContent } from './templateContent';
const formatIcons = {
  Undo: UndoIcon,
  Redo: RedoIcon,
  Bold: BoldIcon,
  Italic: ItalicIcon,
  Underline: UnderlineIcon,
  'Align left': LeftIcon,
  'Align center': CenterIcon,
  'Align right': RightIcon,
  Bullets: ListIcon,
  Numbered: NumberIcon,
  Quote: QuoteIcon,
  Link: LinkIcon,
  Unlink: UnlinkIcon,
  Table: TableIcon,
  Divider: LineIcon,
};

export default function TiptapCanvas({
  value,
  editorDocument,
  onChange,
  onReady,
  onUploadImage,
  onBusy,
}) {
  const callback = React.useRef(onChange);
  callback.current = onChange;
  const last = React.useRef(value);
  const blocked = React.useMemo(
    () => unsupportedMarkup(templateContent(value)),
    [value]
  );
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [url, setUrl] = React.useState('');
  const [marketingOpen, setMarketingOpen] = React.useState(false);
  const [provider, setProvider] = React.useState('facebook');
  const [pixel, setPixel] = React.useState('');
  const [message, setMessage] = React.useState('');
  // Keep the engine options stable: changing node-view/extension identities on
  // each React render makes Tiptap rebuild views and can trigger another render.
  const options = React.useMemo(
    () => ({
      extensions: composerExtensions(),
      shouldRerenderOnTransaction: true,
      content: blocked ? '' : loadEditorDocument(value, editorDocument),
      editable: !blocked,
      editorProps: {
        attributes: {
          'aria-label': 'LifeObject visual content',
          role: 'textbox',
          'aria-multiline': 'true',
        },
        handleClick: (_, __, event) => {
          if (event.target.closest('a')) {
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor: instance }) => {
        last.current = instance.getHTML();
        callback.current(last.current, instance.getJSON());
      },
    }),
    []
  );
  const editor = useEditor(options);
  React.useEffect(() => {
    if (!editor) return;
    // Permission/prop synchronization is not a document edit. Emitting update
    // here feeds serialized HTML back into the parent and starts another sync.
    if (editor.isEditable !== !blocked) editor.setEditable(!blocked, false);
    if (value !== last.current) {
      last.current = value;
      if (!blocked)
        editor.commands.setContent(loadEditorDocument(value, editorDocument), {
          emitUpdate: false,
        });
    }
  }, [editor, value, blocked, editorDocument]);
  React.useEffect(() => {
    if (editor && !blocked)
      onReady({
        insertContent: (html) =>
          editor.chain().focus().insertContent(html).run(),
        appendTemplate: (html) =>
          editor
            .chain()
            .focus()
            .insertContentAt(editor.state.doc.content.size, html)
            .run(),
        getSelection: () => {
          const { from, to } = editor.state.selection;
          return from === to
            ? null
            : { from, to, text: editor.state.doc.textBetween(from, to, '\n') };
        },
        replaceSelection: ({ from, to }, html) =>
          editor.chain().focus().insertContentAt({ from, to }, html).run(),
      });
    return () => onReady(null);
  }, [editor, onReady, blocked]);
  if (!editor) return <p>Preparing your canvas…</p>;
  if (blocked)
    return (
      <div className="lt-composer-error">
        This legacy layout contains elements that need HTML mode. Its original
        HTML is preserved. Use HTML or Preview above; no automatic conversion
        has been applied.
      </div>
    );
  const button = (label, run, active = false, disabled = false) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={run}
    >
      {formatIcons[label]
        ? React.createElement(formatIcons[label], { style: { fontSize: 19 } })
        : label}
    </button>
  );
  const applyLink = () => {
    if (!/^(https?:|mailto:|tel:)/i.test(url)) {
      setMessage('Use an https://, mailto:, or tel: address.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkOpen(false);
    setMessage('');
  };
  const sections = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'layout') return;
    let target = null;
    node.descendants((child, relativePos) => {
      if (target === null && child.isTextblock) target = pos + relativePos + 2;
    });
    if (target !== null)
      sections.push({
        target,
        label:
          node.attrs.label || node.textContent.trim().slice(0, 55) || 'Section',
      });
  });
  return (
    <div className="lt-tiptap">
      {sections.length > 0 && (
        <div className="lt-section-tools">
          <label htmlFor="lt-section-picker">Edit a section</label>
          <select
            id="lt-section-picker"
            aria-label="Go to section"
            value=""
            onChange={(event) =>
              editor
                .chain()
                .focus()
                .setTextSelection(Number(event.target.value))
                .scrollIntoView()
                .run()
            }
          >
            <option value="" disabled>
              Choose a section…
            </option>
            {sections.map((section, index) => (
              <option key={`${section.target}-${index}`} value={section.target}>
                {index + 1}. {section.label}
              </option>
            ))}
          </select>
          <span>Click text to edit it.</span>
        </div>
      )}
      <div
        className="lt-format-toolbar"
        role="toolbar"
        aria-label="Text formatting"
      >
        {button(
          'Undo',
          () => editor.chain().focus().undo().run(),
          false,
          !editor.can().undo()
        )}
        {button(
          'Redo',
          () => editor.chain().focus().redo().run(),
          false,
          !editor.can().redo()
        )}
        <select
          aria-label="Text style"
          value={
            editor.isActive('heading', { level: 1 })
              ? '1'
              : editor.isActive('heading', { level: 2 })
              ? '2'
              : editor.isActive('heading', { level: 3 })
              ? '3'
              : 'p'
          }
          onChange={(e) =>
            e.target.value === 'p'
              ? editor.chain().focus().setParagraph().run()
              : editor
                  .chain()
                  .focus()
                  .setHeading({ level: Number(e.target.value) })
                  .run()
          }
        >
          <option value="p">Paragraph</option>
          <option value="1">Title</option>
          <option value="2">Heading</option>
          <option value="3">Subheading</option>
        </select>
        <select
          aria-label="Font family"
          title="Choose a font for selected text or what you type next"
          style={{
            fontFamily: editor.getAttributes('textStyle').fontFamily || 'Arial',
          }}
          onChange={(e) =>
            editor.chain().focus().setFontFamily(e.target.value).run()
          }
          value={editor.getAttributes('textStyle').fontFamily || ''}
        >
          <option value="" disabled>
            Choose font
          </option>
          <optgroup label="Clean & modern">
            <option value="Arial" style={{ fontFamily: 'Arial' }}>
              Arial
            </option>
            <option value="Verdana" style={{ fontFamily: 'Verdana' }}>
              Verdana
            </option>
            <option value="Trebuchet MS" style={{ fontFamily: 'Trebuchet MS' }}>
              Trebuchet
            </option>
          </optgroup>
          <optgroup label="Editorial & classic">
            <option value="Georgia" style={{ fontFamily: 'Georgia' }}>
              Georgia
            </option>
            <option
              value="Times New Roman"
              style={{ fontFamily: 'Times New Roman' }}
            >
              Times New Roman
            </option>
            <option
              value="Palatino Linotype"
              style={{ fontFamily: 'Palatino Linotype' }}
            >
              Palatino
            </option>
          </optgroup>
          <optgroup label="Distinctive">
            <option value="Courier New" style={{ fontFamily: 'Courier New' }}>
              Courier New
            </option>
            <option value="Impact" style={{ fontFamily: 'Impact' }}>
              Impact
            </option>
          </optgroup>
        </select>
        <select
          aria-label="Font size"
          defaultValue="17px"
          onChange={(e) =>
            editor.chain().focus().setFontSize(e.target.value).run()
          }
        >
          {['12', '14', '17', '20', '24', '32', '40', '48', '64'].map((s) => (
            <option key={s} value={`${s}px`}>
              {s}
            </option>
          ))}
        </select>
        {button(
          'Bold',
          () => editor.chain().focus().toggleBold().run(),
          editor.isActive('bold')
        )}
        {button(
          'Italic',
          () => editor.chain().focus().toggleItalic().run(),
          editor.isActive('italic')
        )}
        {button(
          'Underline',
          () => editor.chain().focus().toggleUnderline().run(),
          editor.isActive('underline')
        )}
        <label title="Text color">
          Color{' '}
          <input
            aria-label="Text color"
            type="color"
            defaultValue="#253e34"
            onChange={(e) =>
              editor.chain().focus().setColor(e.target.value).run()
            }
          />
        </label>
        {['left', 'center', 'right'].map((align) => (
          <React.Fragment key={align}>
            {button(`Align ${align}`, () =>
              editor.chain().focus().setTextAlign(align).run()
            )}
          </React.Fragment>
        ))}
        {button(
          'Bullets',
          () => editor.chain().focus().toggleBulletList().run(),
          editor.isActive('bulletList')
        )}
        {button(
          'Numbered',
          () => editor.chain().focus().toggleOrderedList().run(),
          editor.isActive('orderedList')
        )}
        {button(
          'Quote',
          () => editor.chain().focus().toggleBlockquote().run(),
          editor.isActive('blockquote')
        )}
        {button(
          'Link',
          () => {
            setUrl(editor.getAttributes('link').href || 'https://');
            setLinkOpen(!linkOpen);
          },
          editor.isActive('link')
        )}
        {button(
          'Unlink',
          () => editor.chain().focus().unsetLink().run(),
          false,
          !editor.isActive('link')
        )}
        {button('Divider', () =>
          editor.chain().focus().setHorizontalRule().run()
        )}
        {button('Table', () =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 2, cols: 2, withHeaderRow: false })
            .run()
        )}
        {button('Marketing', () => setMarketingOpen(!marketingOpen))}
        {editor.isActive('table') && (
          <>
            {button('Add row', () =>
              editor.chain().focus().addRowAfter().run()
            )}
            {button('Add column', () =>
              editor.chain().focus().addColumnAfter().run()
            )}
            {button('Delete table', () =>
              editor.chain().focus().deleteTable().run()
            )}
          </>
        )}
      </div>
      {linkOpen && (
        <div className="lt-inline-tools">
          <input
            aria-label="Link destination"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button onClick={applyLink}>Apply link</button>
          <button onClick={() => setLinkOpen(false)}>Cancel</button>
        </div>
      )}
      {marketingOpen && (
        <div className="lt-inline-tools">
          <select
            aria-label="Marketing provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="facebook">Meta Pixel</option>
            <option value="google">Google Analytics</option>
            <option value="twitter">X Pixel</option>
            <option value="hubspot">HubSpot</option>
          </select>
          <input
            aria-label="Marketing ID"
            value={pixel}
            onChange={(e) => setPixel(e.target.value)}
            placeholder="Your account or pixel ID"
          />
          <button
            disabled={!/^[a-zA-Z0-9-]+$/.test(pixel)}
            onClick={() => {
              editor
                .chain()
                .focus()
                .insertContent({
                  type: 'marketing',
                  attrs: {
                    config: JSON.stringify({ type: provider, pixelId: pixel }),
                  },
                })
                .run();
              setMarketingOpen(false);
            }}
          >
            Add integration
          </button>
          <p>
            Stored with this layout. Nothing fires in the editor; published
            consent and delivery remain separate.
          </p>
        </div>
      )}
      {message && <p role="alert">{message}</p>}
      <CanvasInspector
        editor={editor}
        onUploadImage={onUploadImage}
        onBusy={onBusy}
      />
      <EditorContent editor={editor} />
    </div>
  );
}
